import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Shield, Lock } from "lucide-react";

export default function LoginPage() {
  const { login, isAuthenticated, demoMode } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/");
    }
  }, [isAuthenticated, navigate]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #00D4E4 0%, #3C02CC 100%)',
      }}
    >
      {/* Grid texture */}
      <div className="absolute inset-0 opacity-[0.025] pointer-events-none"
        style={{
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.15) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
        }}
      />
      {/* Vignette */}
      <div className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at center, transparent 30%, rgba(0,0,0,0.1) 100%)',
        }}
      />

      {/* Main card */}
      <div
        className="w-full max-w-md bg-white rounded-3xl p-10 space-y-8 relative z-10 animate-in fade-in slide-in-from-bottom-4 duration-500"
        style={{
          boxShadow: '0 25px 80px rgba(0,0,0,0.12), 0 8px 24px rgba(0,0,0,0.06)',
        }}
      >
        {/* Logo */}
        <div className="flex justify-center pb-2">
          <img src="/assets/alice-logo-royal.svg" alt="ALICE" className="h-36 w-36" />
        </div>

        {/* Headline */}
        <div className="text-center space-y-1.5">
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Welcome back</h1>
          <p className="text-gray-500 text-sm">Sign in to continue to ALICE</p>
        </div>

        {/* Microsoft SSO button */}
        <div className="space-y-2.5">
          <Button
            onClick={login}
            variant="outline"
            className="w-full h-12 bg-white hover:bg-[#BBFF71] text-gray-800 hover:text-[#3C02CC] font-semibold rounded-xl border-2 border-gray-200 hover:border-[#BBFF71] flex items-center justify-center gap-3 text-sm transition-all duration-200 hover:shadow-md"
            data-testid="microsoft-login-btn"
          >
            <svg className="h-5 w-5 shrink-0" viewBox="0 0 21 21" fill="none">
              <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
              <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
              <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
              <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
            </svg>
            Sign in with Microsoft
          </Button>
          <p className="text-center text-xs text-gray-400">Use your work Microsoft account</p>
        </div>

        {demoMode && (
          <div className="text-center pt-1">
            <div className="relative flex items-center justify-center mb-3">
              <div className="border-t border-gray-200 w-full absolute" />
              <span className="bg-white px-3 text-xs text-gray-400 relative">or</span>
            </div>
            <button
              onClick={login}
              className="text-sm text-[#3C02CC] hover:underline font-medium"
              data-testid="demo-login-btn"
            >
              Continue with Demo Mode
            </button>
          </div>
        )}

        {/* Divider */}
        <div className="border-t border-gray-100" />

        {/* Security trust block */}
        <div className="bg-gray-50 rounded-xl p-4 space-y-2 border border-gray-200/80">
          <div className="flex items-center gap-2">
            <Shield className="h-4 w-4 text-[#006AF2]" />
            <span className="text-sm font-semibold text-gray-700">Secure login powered by Microsoft</span>
          </div>
          <p className="text-xs text-gray-400 leading-relaxed">
            Authentication is handled securely via Microsoft Entra ID (Azure AD).
          </p>
          <div className="flex items-center justify-center gap-1.5 pt-1">
            <Lock className="h-3 w-3 text-green-500" />
            <span className="text-xs font-medium text-green-600">Enterprise security</span>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-10 text-center space-y-2.5 relative z-10">
        <p className="text-sm text-white/80 drop-shadow-sm">
          Need help?{" "}
          <a href="mailto:support@beyondapplications.io" className="text-white font-medium hover:underline">
            support@beyondapplications.io
          </a>
        </p>
        <div className="flex items-center justify-center gap-4 text-xs text-white/60">
          <a href="#" className="hover:text-white hover:underline">Terms</a>
          <span>·</span>
          <a href="#" className="hover:text-white hover:underline">Privacy</a>
        </div>
      </div>
    </div>
  );
}
